import React from 'react';
import { observer } from 'mobx-react';
import { LinearProgress } from '@mui/material';

import UploadAttachment from 'components/Entities/EntityAttachmentTab/components/UploadAttachment';
import { AnswerTypes } from 'utils/api/modules/incidents/incidents.types';
import FileContainer from 'components/Entities/EntityAttachmentTab/components/FileContainer';
import { ActivityType } from 'utils/stores/Activities/constants';
import { ProtectorTypeAttachmentProps } from 'components/SharedFormComponents/types';
import { useStore } from 'utils/hooks/store';
import { FileUploadProgressResponse } from 'ui/types';
import FileUploadProgress from 'components/Entities/EntityAttachmentTab/components/FileUploadProgress';
import { UserNameResponse } from 'utils/api/modules/user/user.types';
import { FileUploadStatus } from 'stores/EntitiesFormSubmission/FileUploadManager';

const CustomAttachmentField = (props: ProtectorTypeAttachmentProps): React.ReactElement => {
  const { session } = useStore();

  const { value, onChange, fieldKey } = props;
  const {
    entitiesFormSubmission: { fileUploadManager, isPublicSubmission },
  } = useStore();

  const uploadFiles = async (files: File[]): Promise<void> => {
    const existingFiles = value && Array.isArray(value) ? value : [];

    // Create a map of new files by filename for quick lookup
    const newFilesMap = new Map(files.map((file) => [file.name, { filename: file.name, size: file.size }]));

    // Filter out existing files that will be replaced (have same filename as new files)
    const filteredExistingFiles = existingFiles.filter((existingFile) => !newFilesMap.has(existingFile.filename));

    // Combine new files with remaining existing files
    const data = [
      ...Array.from(newFilesMap.values()),
      ...filteredExistingFiles, // Preserve all properties including url for existing files that are not being replaced
    ] as unknown as AnswerTypes;

    onChange(data, undefined, files);
  };

  const uploadedFiles = value && Array.isArray(value) ? value : [];

  // For public submissions, we don't track file upload states since files are saved client-side
  // and sent together when the form is submitted
  const fieldUploads = !isPublicSubmission && fieldKey ? fileUploadManager.getFieldUploads(fieldKey) : [];
  const uploadByFilename = new Map(fieldUploads.map((upload) => [upload.file.name, upload]));
  const uploadedFilenames = new Set(uploadedFiles.map((file) => file.filename));

  const isDeleting = (filename: string): boolean =>
    uploadByFilename.get(filename)?.status === FileUploadStatus.Deleting;

  let completeFiles = uploadedFiles;
  let pendingUploads: { file: File; size: number; status: FileUploadStatus }[] = [];

  if (!isPublicSubmission) {
    completeFiles = uploadedFiles.filter((file) => file.url && !isDeleting(file.filename));

    // Derive "pending" from the value's own url rather than fileUploadManager membership alone,
    // so a file still renders even after the manager has already dropped its upload entry.
    const pendingFromValue = uploadedFiles
      .filter((file) => !file.url && !isDeleting(file.filename))
      .map((file) => {
        const upload = uploadByFilename.get(file.filename);
        return {
          file: upload?.file ?? new File([], file.filename),
          size: file.size,
          status: upload?.status ?? FileUploadStatus.Uploading,
        };
      });
    const pendingFromManagerOnly = fieldUploads
      .filter(
        (upload) =>
          !uploadedFilenames.has(upload.file.name) &&
          upload.status !== FileUploadStatus.Success &&
          upload.status !== FileUploadStatus.Deleting
      )
      .map((upload) => ({ file: upload.file, size: upload.file.size, status: upload.status }));
    pendingUploads = [...pendingFromValue, ...pendingFromManagerOnly];
  }

  const retryUpload = (file: File): void => {
    if (fieldKey && file.size > 0) {
      fileUploadManager.updateStatus(fieldKey, file.name, FileUploadStatus.Pending);
      uploadFiles([file]);
    }
  };

  const dismissFile = (filename: string): void => {
    if (fieldKey) {
      fileUploadManager.removeFile(fieldKey, filename);
      // remove the file from the uploaded files
      const updatedFiles = uploadedFiles.filter((file) => file.filename !== filename);
      onChange(updatedFiles, undefined, undefined);
    }
  };

  const deleteFile = (filename: string): void => {
    if (!fieldKey) return;

    // For non-public submissions, track deletion state
    if (!isPublicSubmission) {
      const placeholderFile = new File([], filename);
      fileUploadManager.addFiles(fieldKey, [placeholderFile]);
      fileUploadManager.updateStatus(fieldKey, filename, FileUploadStatus.Deleting);
    }

    const updatedFiles = uploadedFiles.filter((file) => file.filename !== filename);
    onChange(updatedFiles, undefined, undefined);
  };

  const created = new Date().toISOString();

  return (
    <>
      <UploadAttachment uploadFiles={uploadFiles} multiple />
      {!isPublicSubmission && fileUploadManager.isFieldDeleting(fieldKey || '') && <LinearProgress />}
      {completeFiles.map((file) => (
        <FileContainer
          key={file.filename}
          attachment={{
            filename: file.filename,
            size: file.size,
            type: ActivityType.File,
            file: file.url,
            uuid: file.filename,
            created: null,
          }}
          deleteFile={() => deleteFile(file.filename)}
          deleteWithoutConfirmation
          disableDownload={isPublicSubmission}
        />
      ))}

      {pendingUploads.map(({ file, size, status }) => (
        <FileUploadProgress
          key={file.name}
          fileActivity={
            {
              uuid: file.name,
              type: ActivityType.FileUploadProgress,
              creator: session?.user as UserNameResponse,
              created,
              name: file.name,
              size,
              loaded: 0,
            } as FileUploadProgressResponse
          }
          indeterminate={status === FileUploadStatus.Pending || status === FileUploadStatus.Uploading}
          error={status === FileUploadStatus.Failed}
          onRetry={status === FileUploadStatus.Failed ? () => retryUpload(file) : undefined}
          onDismiss={status === FileUploadStatus.Failed ? () => dismissFile(file.name) : undefined}
        />
      ))}
    </>
  );
};

export default observer(CustomAttachmentField);
